import React from 'react';
import { useTheme } from '../../context/shared/ThemeContext'

export interface PatientFormData {
  displayName: string;
  email: string;
  dob: string;
  sex: string;
  pronouns: string;
  phoneNumber: string;
  street1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  primaryConcerns: string;
  pastDiagnoses: string;
  medicationList: string;
  treatmentPlanSummary: string;
  // Additional medical fields
  allergies: string;
  conditions: string;
  insurance: string;
  primaryCarePhysician: string;
  lastPhysical: string;
  // Additional clinical fields
  goals: string;
  riskFactors: string;
  lastSession: string;
  nextSession: string;
  therapist: string;
  // Consents
  consentMetrics: boolean;
  consentMessaging: boolean;
}

interface PatientFormProps {
  formData: PatientFormData;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const PatientForm: React.FC<PatientFormProps> = ({ formData, onFormChange }) => {
  const { themeClasses } = useTheme();
  const inputClass = `mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${themeClasses.input}`;

  return (
    <form className="space-y-6">
        {/* Section: Basic Information */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Basic Information</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                <label htmlFor="displayName" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Full Name</label>
                <input type="text" name="displayName" id="displayName" value={formData.displayName} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                <label htmlFor="email" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Email Address</label>
                <input type="email" name="email" id="email" value={formData.email} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                <label htmlFor="dob" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Date of Birth</label>
                <input type="date" name="dob" id="dob" value={formData.dob} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                <label htmlFor="sex" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Sex</label>
                <input type="text" name="sex" id="sex" value={formData.sex} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                <label htmlFor="pronouns" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Pronouns (Optional)</label>
                <input type="text" name="pronouns" id="pronouns" value={formData.pronouns} onChange={onFormChange} className={inputClass} />
                </div>
            </div>
        </fieldset>

        {/* Section: Contact Details */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Contact Details</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div>
                    <label htmlFor="phoneNumber" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Phone Number</label>
                    <input type="tel" name="phoneNumber" id="phoneNumber" value={formData.phoneNumber} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="street1" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Street Address</label>
                    <input type="text" name="street1" id="street1" value={formData.street1} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="city" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>City</label>
                    <input type="text" name="city" id="city" value={formData.city} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="state" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>State / Province</label>
                    <input type="text" name="state" id="state" value={formData.state} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="zip" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Zip / Postal Code</label>
                    <input type="text" name="zip" id="zip" value={formData.zip} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="country" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Country</label>
                    <input type="text" name="country" id="country" value={formData.country} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="emergencyContactName" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Emergency Contact Name</label>
                    <input type="text" name="emergencyContactName" id="emergencyContactName" value={formData.emergencyContactName} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="emergencyContactPhone" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Emergency Contact Phone</label>
                    <input type="tel" name="emergencyContactPhone" id="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={onFormChange} className={inputClass} />
                </div>
            </div>
        </fieldset>

        {/* Section: Clinical Information */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Clinical Information</legend>
            <div className="space-y-4 mt-2">
                <div>
                    <label htmlFor="primaryConcerns" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Primary Concerns</label>
                    <textarea name="primaryConcerns" id="primaryConcerns" value={formData.primaryConcerns} onChange={onFormChange} className={inputClass} rows={3}></textarea>
                </div>
                <div>
                    <label htmlFor="pastDiagnoses" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Past Diagnoses (comma-separated)</label>
                    <textarea name="pastDiagnoses" id="pastDiagnoses" value={formData.pastDiagnoses} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div>
                    <label htmlFor="medicationList" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Medications (comma-separated)</label>
                    <textarea name="medicationList" id="medicationList" value={formData.medicationList} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div>
                    <label htmlFor="treatmentPlanSummary" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Treatment Plan Summary</label>
                    <textarea name="treatmentPlanSummary" id="treatmentPlanSummary" value={formData.treatmentPlanSummary} onChange={onFormChange} className={inputClass} rows={3}></textarea>
                </div>
            </div>
        </fieldset>

        {/* Section: Medical Information */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Medical Information</legend>
            <div className="space-y-4 mt-2">
                <div>
                    <label htmlFor="allergies" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Allergies (comma-separated)</label>
                    <textarea name="allergies" id="allergies" value={formData.allergies} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div>
                    <label htmlFor="conditions" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Medical Conditions (comma-separated)</label>
                    <textarea name="conditions" id="conditions" value={formData.conditions} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div>
                    <label htmlFor="insurance" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Insurance Provider</label>
                    <input type="text" name="insurance" id="insurance" value={formData.insurance} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="primaryCarePhysician" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Primary Care Physician</label>
                    <input type="text" name="primaryCarePhysician" id="primaryCarePhysician" value={formData.primaryCarePhysician} onChange={onFormChange} className={inputClass} />
                </div>
                <div>
                    <label htmlFor="lastPhysical" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Last Physical Exam</label>
                    <input type="date" name="lastPhysical" id="lastPhysical" value={formData.lastPhysical} onChange={onFormChange} className={inputClass} />
                </div>
            </div>
        </fieldset>

        {/* Section: Additional Clinical Information */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Additional Clinical Information</legend>
            <div className="space-y-4 mt-2">
                <div>
                    <label htmlFor="goals" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Treatment Goals (comma-separated)</label>
                    <textarea name="goals" id="goals" value={formData.goals} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div>
                    <label htmlFor="riskFactors" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Risk Factors (comma-separated)</label>
                    <textarea name="riskFactors" id="riskFactors" value={formData.riskFactors} onChange={onFormChange} className={inputClass} rows={2}></textarea>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="lastSession" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Last Session</label>
                        <input type="date" name="lastSession" id="lastSession" value={formData.lastSession} onChange={onFormChange} className={inputClass} />
                    </div>
                    <div>
                        <label htmlFor="nextSession" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Next Session</label>
                        <input type="date" name="nextSession" id="nextSession" value={formData.nextSession} onChange={onFormChange} className={inputClass} />
                    </div>
                </div>
                <div>
                    <label htmlFor="therapist" className={`block text-sm font-medium ${themeClasses.secondaryText}`}>Assigned Therapist</label>
                    <input type="text" name="therapist" id="therapist" value={formData.therapist} onChange={onFormChange} className={inputClass} />
                </div>
            </div>
        </fieldset>

        {/* Section: Consents */}
        <fieldset className="border p-4 rounded-lg">
            <legend className={`text-lg font-semibold px-2 ${themeClasses.primaryText}`}>Consents</legend>
            <div className="flex flex-col space-y-3 mt-2">
                <label htmlFor="consentMetrics" className="flex items-center cursor-pointer">
                    <input type="checkbox" name="consentMetrics" id="consentMetrics" checked={formData.consentMetrics} onChange={onFormChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span className={`ml-3 text-sm ${themeClasses.secondaryText}`}>Allow symbolic metrics processing</span>
                </label>
                <label htmlFor="consentMessaging" className="flex items-center cursor-pointer">
                    <input type="checkbox" name="consentMessaging" id="consentMessaging" checked={formData.consentMessaging} onChange={onFormChange} className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
                    <span className={`ml-3 text-sm ${themeClasses.secondaryText}`}>Allow in-app messaging</span>
                </label>
            </div>
        </fieldset>
    </form>
  );
};

export default PatientForm; 